import { tryOnUnmounted } from "@vueuse/core";

/**
 * Wraps an async function so that each new call aborts the previous one
 * via AbortSignal. Also aborts on component unmount.
 *
 * @returns {<T>(fn: (signal: AbortSignal) => T | Promise<T>) => Promise<T | null>}
 *   A function that runs `fn(signal)` and returns its result. Returns `null`
 *   if the call was aborted (either by a newer call or by unmount).
 *
 * @example
 *   const abortable = useAbortable();
 *   const search = q => abortable(signal => store.getSegments(q, { signal }));
 */
export function useAbortable() {
  /** @type {AbortController | null} */
  let controller = null;

  const cancel = () => {
    controller?.abort();
    controller = null;
  };

  tryOnUnmounted(cancel);

  return async fn => {
    cancel();
    const local = new AbortController();
    controller = local;
    try {
      return await fn(local.signal);
    } catch (err) {
      if (err?.name === "AbortError") return null;
      throw err;
    } finally {
      // Only clear if this call is still the active one — guards against
      // a newer call having replaced `controller` while we were awaiting.
      if (controller === local) controller = null;
    }
  };
}
